import { isNil, isNoResponseError } from '@lucidlink/tscore';
import { HTTPScheme, NotImplementedError } from '#common/types';
import { createServerCryptoProvider, CryptoTuple } from '#crypto';
import { KeyRangeLock } from '#key-range-locking';
import { LockType } from '#key-range-locking/types';
import { AccessDeniedError, AuthorizationError } from '#object-store/types';
import { BucketObjectsHelper } from './bucket-objects-helper';
import { ListDataStoresHelper } from './list-data-stores-helper';
import { PresignedUrlHelper } from './presigned-url-helper';
import { RekeyValidator } from './rekey-validator';
import { DataStoreAlreadyExistsError, DataStoreHasLinkedFilesError, DataStoreInvalidRegionError, DataStoreInvalidTypeError, DataStoreKind, DataStoreLimitExceededError, DataStoreNotFoundError, } from './types';
import { normalizeDataStore, normalizeDataStoreName, validateDataStore, validateDataStoreCount, validateDataStoreName, } from './validation';
export const createDataStores = (dataStoresWasm, tupleWasm, kvHelperWasm, authorizer, fileNodeManager, configManager, commonConfigKeyHelper, configValidator, adminCryptoProvider, keyRangeLocking, nodeId, logTelemetry) => {
    return new DataStoresImpl(dataStoresWasm, tupleWasm, kvHelperWasm, authorizer, fileNodeManager, configManager, commonConfigKeyHelper, configValidator, adminCryptoProvider, keyRangeLocking, nodeId, logTelemetry);
};
export class DataStoresImpl {
    dataStoresWasm;
    tupleWasm;
    kvHelperWasm;
    authorizer;
    configValidator;
    logTelemetry;
    serverCrypto;
    adminCrypto;
    keyRangeLock;
    listDataStoresHelper;
    rekeyValidator;
    bucketObjectsHelper;
    constructor(dataStoresWasm, tupleWasm, kvHelperWasm, authorizer, fileNodeManager, configManager, commonConfigKeyHelper, configValidator, adminCryptoProvider, keyRangeLocking, nodeId, logTelemetry) {
        this.dataStoresWasm = dataStoresWasm;
        this.tupleWasm = tupleWasm;
        this.kvHelperWasm = kvHelperWasm;
        this.authorizer = authorizer;
        this.configValidator = configValidator;
        this.logTelemetry = logTelemetry;
        this.serverCrypto = new CryptoTuple(tupleWasm, createServerCryptoProvider(configManager));
        this.adminCrypto = new CryptoTuple(tupleWasm, adminCryptoProvider);
        const lockOwner = { lockId: 0n, nodeId: nodeId };
        const dataStoresRange = this.kvHelperWasm.getDataStoresRange();
        this.keyRangeLock = new KeyRangeLock(keyRangeLocking, lockOwner, LockType.EXCLUSIVE, dataStoresRange);
        this.listDataStoresHelper = new ListDataStoresHelper(dataStoresWasm);
        const presignedUrLHelper = new PresignedUrlHelper(tupleWasm, this.adminCrypto);
        this.rekeyValidator = new RekeyValidator(this, fileNodeManager, kvHelperWasm, commonConfigKeyHelper, presignedUrLHelper);
        this.bucketObjectsHelper = new BucketObjectsHelper(tupleWasm, this.adminCrypto);
    }
    async add(name, dataStore) {
        try {
            return await this.addDataStore(name, dataStore);
        }
        catch (err) {
            this.logTelemetry(DataStoresImpl.makeDataStoreAddFailedEvent(dataStore, err));
            throw err;
        }
    }
    async addDataStore(name, dataStore) {
        await this.configValidator.verifyDataStoresAvailable();
        const normalizedName = normalizeDataStoreName(name);
        const normalizedDataStore = normalizeDataStore(dataStore);
        validateDataStoreName(normalizedName);
        validateDataStore(normalizedDataStore);
        const currentDataStores = await this.list();
        validateDataStoreCount(currentDataStores.length);
        const dataStoreInfo = await this.executeWithAdminPrivilegesAndLock(async () => {
            const encryptedDataStore = await this.encrypt(normalizedDataStore);
            return this.dataStoresWasm.addDataStore(normalizedName, encryptedDataStore);
        });
        this.logTelemetry(DataStoresImpl.makeDataStoreAddedEvent(normalizedName, normalizedDataStore));
        return dataStoreInfo;
    }
    async remove(name) {
        try {
            await this.removeDataStore(name);
        }
        catch (err) {
            this.logTelemetry(DataStoresImpl.makeDataStoreRemoveFailedEvent(err));
            throw err;
        }
    }
    async removeDataStore(name) {
        await this.configValidator.verifyDataStoresAvailable();
        const normalizedName = normalizeDataStoreName(name);
        const removedDataStore = await this.executeWithAdminPrivilegesAndLock(async () => {
            const dataStore = await this.dataStoresWasm.getInternalDataStore(normalizedName);
            await this.dataStoresWasm.removeDataStore(normalizedName);
            return dataStore;
        });
        this.logTelemetry({
            name: 'DataStoreRemoved',
            data: {
                customData: {
                    name: normalizedName,
                    ...(!isNil(removedDataStore) && { kind: removedDataStore.kind }),
                },
            },
        });
    }
    async get(name) {
        await this.configValidator.verifyDataStoresAvailable();
        const normalizedName = normalizeDataStoreName(name);
        return this.authorizer.executeWithAdminPrivileges(() => {
            return this.dataStoresWasm.getInternalDataStore(normalizedName);
        });
    }
    async list() {
        await this.configValidator.verifyDataStoresAvailable();
        return this.authorizer.executeWithAdminPrivileges(() => {
            return this.dataStoresWasm.listDataStores();
        });
    }
    async stats() {
        await this.configValidator.verifyDataStoresAvailable();
        return this.authorizer.executeWithAdminPrivileges(() => {
            return this.dataStoresWasm.listDataStoreStats();
        });
    }
    async initializeRekey(name, credentials) {
        await this.configValidator.verifyDataStoresAvailable();
        const normalizedName = normalizeDataStoreName(name);
        await this.executeWithAdminPrivilegesAndLock(async () => {
            switch (credentials.kind) {
                case DataStoreKind.S3:
                    return await this.initializeRekeyS3(normalizedName, credentials);
                default:
                    throw new NotImplementedError();
            }
        });
        this.logTelemetry({
            name: 'DataStoreRekeyInitiated',
            data: { customData: { name: normalizedName } },
        });
    }
    async listExternalFiles(dataStoreName) {
        await this.configValidator.verifyDataStoresAvailable();
        const normalizedName = normalizeDataStoreName(dataStoreName);
        return this.authorizer.executeWithAdminPrivileges(() => {
            return Promise.resolve(this.listDataStoresHelper.createIterator(normalizedName));
        });
    }
    async listExternalFilesPaginated(dataStoreName, params) {
        await this.configValidator.verifyDataStoresAvailable();
        const normalizedName = normalizeDataStoreName(dataStoreName);
        return this.authorizer.executeWithAdminPrivileges(async () => {
            return this.listDataStoresHelper.listByDataStore(normalizedName, params?.limit, params?.cursor);
        });
    }
    async listObjects(name, params) {
        try {
            return await this.listBucketObjects(name, params);
        }
        catch (err) {
            this.logTelemetry(DataStoresImpl.makeBucketBrowseFailedEvent(err));
            throw err;
        }
    }
    async listBucketObjects(name, params) {
        await this.configValidator.verifyDataStoresAvailable();
        const normalizedName = normalizeDataStoreName(name);
        return this.authorizer.executeWithAdminPrivileges(async () => {
            const dataStore = await this.dataStoresWasm.getInternalDataStore(normalizedName);
            if (isNil(dataStore)) {
                throw new DataStoreNotFoundError(`Data store does not exist: ${normalizedName}`);
            }
            if (dataStore.kind !== DataStoreKind.S3) {
                throw new DataStoreInvalidTypeError(`Data store '${normalizedName}' must be an S3 data store, got: ${dataStore.kind}`);
            }
            return this.bucketObjectsHelper.listObjects(dataStore, params);
        });
    }
    static makeDataStoreAddedEvent(name, dataStore) {
        return {
            name: 'DataStoreAdded',
            data: {
                customData: {
                    name,
                    kind: dataStore.kind,
                    ...(dataStore.kind === DataStoreKind.S3 && {
                        endpointOverride: dataStore.endpointOverride,
                        region: dataStore.region,
                        useVirtualAddressing: dataStore.useVirtualAddressing,
                        httpScheme: dataStore.httpScheme === HTTPScheme.HTTPS ? 'https' : 'http',
                        ...(!isNil(dataStore.urlExpirationMinutes) && {
                            urlExpirationMinutes: dataStore.urlExpirationMinutes,
                        }),
                    }),
                },
            },
        };
    }
    static makeDataStoreAddFailedEvent(dataStore, err) {
        return {
            name: 'DataStoreAddFailed',
            data: {
                customData: {
                    kind: dataStore.kind,
                    errorType: DataStoresImpl.getAddFailedErrorType(err),
                },
            },
        };
    }
    static getAddFailedErrorType(err) {
        if (err instanceof DataStoreAlreadyExistsError) {
            return 'already_exists';
        }
        if (err instanceof DataStoreLimitExceededError) {
            return 'limit_exceeded';
        }
        if (err instanceof DataStoreInvalidRegionError) {
            return 'invalid_region';
        }
        return 'other';
    }
    static makeDataStoreRemoveFailedEvent(err) {
        return {
            name: 'DataStoreRemoveFailed',
            data: {
                customData: {
                    reason: DataStoresImpl.getRemoveFailedReason(err),
                },
            },
        };
    }
    static getRemoveFailedReason(err) {
        if (err instanceof DataStoreHasLinkedFilesError) {
            return 'has_linked_files';
        }
        if (err instanceof DataStoreNotFoundError) {
            return 'not_found';
        }
        return 'other';
    }
    static makeBucketBrowseFailedEvent(err) {
        return {
            name: 'BucketBrowseFailed',
            data: {
                customData: {
                    reason: DataStoresImpl.getBucketBrowseFailedReason(err),
                },
            },
        };
    }
    static getBucketBrowseFailedReason(err) {
        if (err instanceof AccessDeniedError) {
            return 'forbidden';
        }
        if (isNoResponseError(err) || err instanceof AuthorizationError) {
            return 'unreachable';
        }
        return 'generic';
    }
    executeWithAdminPrivilegesAndLock(operation) {
        return this.authorizer.executeWithAdminPrivileges(() => {
            return this.keyRangeLock.execute(async () => {
                return await operation();
            });
        });
    }
    async initializeRekeyS3(name, credentials) {
        const { hubEncryptedSecretKey, adminEncryptedSecretKey } = await this.encryptSecretKey(credentials.secretKey);
        await this.rekeyValidator.validateRekey(name, credentials.accessKey, hubEncryptedSecretKey, adminEncryptedSecretKey);
        return this.dataStoresWasm.initializeRekey(name, credentials.accessKey, hubEncryptedSecretKey, adminEncryptedSecretKey);
    }
    encrypt(dataStore) {
        switch (dataStore.kind) {
            case DataStoreKind.S3:
                return this.encryptS3DataStore(dataStore);
            case DataStoreKind.HTTP_LINK:
                return Promise.resolve(dataStore);
            default:
                throw new NotImplementedError();
        }
    }
    async encryptS3DataStore(dataStore) {
        const { hubEncryptedSecretKey, adminEncryptedSecretKey } = await this.encryptSecretKey(dataStore.secretKey);
        return {
            kind: dataStore.kind,
            accessKey: dataStore.accessKey,
            hubEncryptedSecretKey,
            adminEncryptedSecretKey,
            bucketName: dataStore.bucketName,
            region: dataStore.region,
            endpointOverride: dataStore.endpointOverride,
            httpScheme: dataStore.httpScheme,
            useVirtualAddressing: dataStore.useVirtualAddressing,
            ...(!isNil(dataStore.urlExpirationMinutes) && {
                urlExpirationMinutes: dataStore.urlExpirationMinutes,
            }),
        };
    }
    async encryptSecretKey(secretKey) {
        const tuple = this.tupleWasm.makeTupleFromString(secretKey);
        const hubEncryptedSecretKey = await this.serverCrypto.encrypt(tuple);
        const adminEncryptedSecretKey = await this.adminCrypto.encrypt(tuple);
        return { hubEncryptedSecretKey, adminEncryptedSecretKey };
    }
}
